// Import sequential task functions for use in shouldShowInPrioritize
import { isSequentialTask, canCompleteTask, isBlockedByParentSequentialTask } from './sequentialTasks';

// Build task tree from flat list
export function buildTaskTree(taskList) {
  const taskMap = new Map();
  const rootTasks = [];

  // First pass: create map of all tasks
  taskList.forEach(task => {
    taskMap.set(task.id, { ...task, subtasks: [] });
  });

  // Second pass: build tree structure
  taskList.forEach(task => {
    const taskNode = taskMap.get(task.id);
    if (task.parent_task_id) {
      const parent = taskMap.get(task.parent_task_id);
      if (parent) {
        parent.subtasks.push(taskNode);
      }
    } else {
      rootTasks.push(taskNode);
    }
  });

  // Sort tasks by order (with fallback to created_at for tasks without order)
  const sortTasks = (taskList) => {
    taskList.sort((a, b) => {
      const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
      const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
    // Recursively sort subtasks
    taskList.forEach(task => {
      if (task.subtasks && task.subtasks.length > 0) {
        sortTasks(task.subtasks);
      }
    });
  };

  sortTasks(rootTasks);
  return rootTasks;
}

// Flatten task tree to get visual order
export function flattenTaskTree(taskTree, expandedTasks) {
  const flattened = [];
  const traverse = (taskList) => {
    taskList.forEach(task => {
      flattened.push(task);
      // Check expandedTasks using _reactKey (clientId) instead of id
      if (task.subtasks && task.subtasks.length > 0 && expandedTasks.has(task._reactKey)) {
        traverse(task.subtasks);
      }
    });
  };
  traverse(taskTree);
  return flattened;
}

// Get task with subtasks from tree
export function getTaskWithSubtasks(taskId, tasks) {
  const taskTree = buildTaskTree(tasks);
  const findTaskInTree = (taskList) => {
    for (const task of taskList) {
      if (task.id === taskId) {
        return task;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        const found = findTaskInTree(task.subtasks);
        if (found) return found;
      }
    }
    return null;
  };
  return findTaskInTree(taskTree);
}

/**
 * Find a task in a task tree by its _reactKey (client id).
 * @param {Array} taskTree - Root-level task nodes (each may have subtasks)
 * @param {string} reactKey - _reactKey to find
 * @returns {Object|null} - The task node or null
 */
export function findTaskInTreeByReactKey(taskTree, reactKey) {
  if (!taskTree || !reactKey) return null;
  for (const task of taskTree) {
    if (task._reactKey === reactKey) return task;
    if (task.subtasks?.length) {
      const found = findTaskInTreeByReactKey(task.subtasks, reactKey);
      if (found) return found;
    }
  }
  return null;
}

// Get all subtask IDs recursively
export function getAllSubtaskIds(task) {
  const subtaskIds = [];
  if (task.subtasks && task.subtasks.length > 0) {
    task.subtasks.forEach(subtask => {
      subtaskIds.push(subtask.id);
      subtaskIds.push(...getAllSubtaskIds(subtask));
    });
  }
  return subtaskIds;
}

// Calculate subtask completion stats
// Returns { completed, total, weightedProgress }
// where weightedProgress is the sum of completion values (complete=1.0, wip=0.5, waiting=0.5, incomplete=0.0)
export function getSubtaskStats(task) {
  if (!task.subtasks || task.subtasks.length === 0) {
    return { completed: 0, total: 0, weightedProgress: 0 };
  }
  
  let completed = 0;
  let total = 0;
  let weightedProgress = 0;
  
  const getSubtaskCompletionValue = (subtask) => {
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      const stats = getSubtaskStats(subtask);
      if (stats.total === 0) return 0;
      return stats.weightedProgress / stats.total;
    } else {
      if (subtask.status === 'complete' || subtask.status === 'wont_do') return 1.0;
      if (subtask.status === 'wip' || subtask.status === 'waiting') return 0.5;
      return 0.0;
    }
  };
  
  const isSubtaskComplete = (subtask) => {
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      const stats = getSubtaskStats(subtask);
      return stats.total > 0 && stats.completed === stats.total;
    } else {
      return subtask.status === 'complete' || subtask.status === 'wont_do';
    }
  };
  
  const countSubtasks = (subtask) => {
    total++;
    const completionValue = getSubtaskCompletionValue(subtask);
    weightedProgress += completionValue;
    if (isSubtaskComplete(subtask)) {
      completed++;
    }
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      subtask.subtasks.forEach(countSubtasks);
    }
  };
  
  task.subtasks.forEach(countSubtasks);
  return { completed, total, weightedProgress };
}

// Get subtask status breakdown for progress indicator
// Returns array of { status, count, percentage } sorted by priority
export function getSubtaskStatusBreakdown(task, tasks = []) {
  if (!task.subtasks || task.subtasks.length === 0) {
    return [];
  }
  
  const statusCounts = {
    'complete': 0,
    'wont_do': 0,
    'wip': 0,
    'waiting': 0,
    'incomplete': 0,
    'blocked': 0
  };
  
  let total = 0;
  
  const countSubtasks = (subtask) => {
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      // For parent tasks, recursively count their subtasks
      subtask.subtasks.forEach(countSubtasks);
    } else {
      // For leaf tasks, count by status
      total++;
      const status = subtask.status || 'incomplete';
      
      // Check if subtask is blocked (can't be completed and is incomplete)
      // Only show blocked for incomplete tasks, not for wip/waiting tasks
      const isBlocked = tasks.length > 0 && status === 'incomplete' && !canCompleteTask(subtask.id, tasks);
      
      if (isBlocked) {
        statusCounts['blocked']++;
      } else if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts['incomplete']++;
      }
    }
  };
  
  task.subtasks.forEach(countSubtasks);
  
  if (total === 0) {
    return [];
  }
  
  // Create array of status groups, filtering out zero counts
  // Order: complete, wont_do, wip, waiting, incomplete, blocked
  const statusOrder = ['complete', 'wont_do', 'wip', 'waiting', 'incomplete', 'blocked'];
  const breakdown = statusOrder
    .filter(status => statusCounts[status] > 0)
    .map(status => ({
      status,
      count: statusCounts[status],
      percentage: statusCounts[status] / total
    }));
  
  return breakdown;
}

// Calculate and format progress status for saving to database
// Returns object with completed, total, weightedProgress, and statusBreakdown
export function calculateFileProgressStatus(tasks, fileId) {
  const fileStats = getFileLeafTaskStats(tasks, fileId);
  
  if (fileStats.total === 0) {
    return null;
  }
  
  // Get all tasks from the file
  const fileTasks = tasks.filter(task => task.file_id === fileId);
  
  // Build a set of task IDs that have children (are parents)
  const parentTaskIds = new Set();
  fileTasks.forEach(task => {
    if (task.parent_task_id) {
      parentTaskIds.add(task.parent_task_id);
    }
  });
  
  // Get all leaf tasks (tasks that are not parents)
  const leafTasks = fileTasks
    .filter(task => !parentTaskIds.has(task.id))
    .map(task => ({ ...task, subtasks: [] }));
  
  // Create virtual task with leaf tasks as subtasks
  const virtualTask = {
    id: `file-virtual-task-${fileId}`,
    subtasks: leafTasks
  };
  
  // Get status breakdown
  const statusBreakdown = getSubtaskStatusBreakdown(virtualTask, tasks);
  
  return {
    completed: fileStats.completed,
    total: fileStats.total,
    weightedProgress: fileStats.weightedProgress,
    statusBreakdown: statusBreakdown
  };
}

// Get all leaf tasks (tasks without subtasks) from a file
// Returns array of tasks that don't have subtasks
export function getLeafTasksFromFile(tasks, fileId) {
  const fileTasks = tasks.filter(task => task.file_id === fileId);
  const taskMap = new Map();
  
  // Build task map
  fileTasks.forEach(task => {
    taskMap.set(task.id, { ...task, hasSubtasks: false });
  });
  
  // Mark tasks that have subtasks
  fileTasks.forEach(task => {
    if (task.parent_task_id) {
      const parent = taskMap.get(task.parent_task_id);
      if (parent) {
        parent.hasSubtasks = true;
      }
    }
  });
  
  // Return only leaf tasks (tasks without subtasks)
  return Array.from(taskMap.values()).filter(task => !task.hasSubtasks);
}

// Calculate stats for leaf tasks in a file
// Returns { completed, total, weightedProgress }
export function getFileLeafTaskStats(tasks, fileId) {
  const leafTasks = getLeafTasksFromFile(tasks, fileId);
  
  if (leafTasks.length === 0) {
    return { completed: 0, total: 0, weightedProgress: 0 };
  }
  
  let completed = 0;
  let total = leafTasks.length;
  let weightedProgress = 0;
  
  leafTasks.forEach(task => {
    const status = task.status || 'incomplete';
    if (status === 'complete' || status === 'wont_do') {
      completed++;
      weightedProgress += 1.0;
    } else if (status === 'wip' || status === 'waiting') {
      weightedProgress += 0.5;
    } else {
      weightedProgress += 0.0;
    }
  });
  
  return { completed, total, weightedProgress };
}

/**
 * File progress stats for display (e.g. ProgressIndicator).
 * Returns null when tasks or fileId are missing.
 * When the file has no tasks, returns a placeholder { completed: 0, total: 1, weightedProgress: 0 }
 * so the progress ring can show 0/1 instead of hiding.
 */
export function getFileProgressStats(tasks, fileId) {
  if (!tasks || fileId == null) return null;
  const stats = getFileLeafTaskStats(tasks, fileId);
  if (stats && stats.total === 0) {
    const fileTasks = tasks.filter(task => task.file_id === fileId);
    if (fileTasks.length === 0) {
      return { completed: 0, total: 1, weightedProgress: 0 };
    }
  }
  return stats;
}

/**
 * Builds a virtual task whose subtasks are the file's leaf tasks, for use with ProgressIndicator.
 * Returns null when tasks, fileId, or fileStats are missing.
 * @param {string} [virtualTaskId='file-virtual-task'] - Id for the virtual task object
 */
export function buildVirtualTaskForProgress(tasks, fileId, fileStats, virtualTaskId = 'file-virtual-task') {
  if (!tasks || fileId == null || !fileStats) return null;
  const fileTasks = tasks.filter(task => task.file_id === fileId);
  if (fileTasks.length === 0 && fileStats.total === 1) {
    return {
      id: virtualTaskId,
      subtasks: [{ id: 'placeholder', text: '', status: 'incomplete', subtasks: [] }]
    };
  }
  const parentTaskIds = new Set();
  fileTasks.forEach(task => {
    if (task.parent_task_id) parentTaskIds.add(task.parent_task_id);
  });
  const leafTasks = fileTasks
    .filter(task => !parentTaskIds.has(task.id))
    .map(task => ({ ...task, subtasks: [] }));
  return { id: virtualTaskId, subtasks: leafTasks };
}

// Get status breakdown for leaf tasks in a file
// Returns array of { status, count, percentage } sorted by priority
export function getFileLeafTaskStatusBreakdown(tasks, fileId) {
  const leafTasks = getLeafTasksFromFile(tasks, fileId);
  
  if (leafTasks.length === 0) {
    return [];
  }
  
  const statusCounts = {
    'complete': 0,
    'wont_do': 0,
    'wip': 0,
    'waiting': 0,
    'incomplete': 0
  };
  
  leafTasks.forEach(task => {
    const status = task.status || 'incomplete';
    if (statusCounts.hasOwnProperty(status)) {
      statusCounts[status]++;
    } else {
      statusCounts['incomplete']++;
    }
  });
  
  const total = leafTasks.length;
  
  // Create array of status groups, filtering out zero counts
  // Order: complete, wont_do, wip, waiting, incomplete
  const statusOrder = ['complete', 'wont_do', 'wip', 'waiting', 'incomplete'];
  const breakdown = statusOrder
    .filter(status => statusCounts[status] > 0)
    .map(status => ({
      status,
      count: statusCounts[status],
      percentage: statusCounts[status] / total
    }));
  
  return breakdown;
}

// Check if a task is incomplete (including checking subtasks recursively)
export function isTaskIncomplete(task) {
  if (task.subtasks && task.subtasks.length > 0) {
    return task.subtasks.some(subtask => isTaskIncomplete(subtask));
  } else {
    return task.status !== 'complete' && task.status !== 'wont_do';
  }
}

// Check if a task has status 'incomplete' (not wip, waiting, or complete)
// For tasks with subtasks, check if any subtask has status 'incomplete'
export function hasIncompleteStatus(task) {
  if (task.subtasks && task.subtasks.length > 0) {
    return task.subtasks.some(subtask => hasIncompleteStatus(subtask));
  } else {
    return task.status === 'incomplete';
  }
}

// Check if a task should be hidden in the incomplete filter (is blocked)
// Returns true if the task should be hidden (all incomplete items are blocked)
// Returns false if the task should be shown (has at least one incomplete item that is not blocked)
export function isTaskBlocked(task, tasks) {
  // For tasks with subtasks, only check the leaf incomplete subtasks
  if (task.subtasks && task.subtasks.length > 0) {
    // Find all leaf tasks (tasks without subtasks) that have incomplete status
    const incompleteLeafTasks = [];
    const collectIncompleteLeaves = (subtask) => {
      if (subtask.subtasks && subtask.subtasks.length > 0) {
        // Recurse into subtasks
        subtask.subtasks.forEach(collectIncompleteLeaves);
      } else {
        // This is a leaf task
        if (subtask.status === 'incomplete') {
          incompleteLeafTasks.push(subtask);
        }
      }
    };
    task.subtasks.forEach(collectIncompleteLeaves);
    
    // If there are no incomplete leaf tasks, hide the task
    if (incompleteLeafTasks.length === 0) {
      return true;
    }
    
    // Check if all incomplete leaf tasks are blocked
    // If any is not blocked, show the task (return false)
    const allBlocked = incompleteLeafTasks.every(leafTask => {
      const canComplete = canCompleteTask(leafTask.id, tasks);
      const isComplete = leafTask.status === 'complete';
      return !canComplete && !isComplete;
    });
    
    return allBlocked;
  }
  
  // For tasks without subtasks (leaf tasks), check if the task itself is blocked
  const canComplete = canCompleteTask(task.id, tasks);
  const isComplete = task.status === 'complete';
  return !canComplete && !isComplete;
}

// Check if a task matches the time filter
export function matchesTimeFilter(task, timeFilterType) {
  if (!timeFilterType) return true;
  
  if (!task.due_date) return false;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dateStr = task.due_date.split('T')[0];
  const [year, month, day] = dateStr.split('-');
  const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  dueDate.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (timeFilterType === 'overdue') {
    return daysDiff < 0;
  } else if (timeFilterType === 'today') {
    return daysDiff === 0;
  } else if (timeFilterType === 'next7days') {
    return daysDiff >= 0 && daysDiff <= 7;
  }
  
  return true;
}

// Check if a task or any of its subtasks match the time filter
export function taskOrSubtasksMatchTimeFilter(task, timeFilterType) {
  if (matchesTimeFilter(task, timeFilterType)) {
    return true;
  }
  
  if (task.subtasks && task.subtasks.length > 0) {
    return task.subtasks.some(subtask => taskOrSubtasksMatchTimeFilter(subtask, timeFilterType));
  }
  
  return false;
}

// Filter task tree based on the selected filter
export function filterTaskTree(taskTree, filterType, timeFilterType) {
  const processTasks = (taskList) => {
    return taskList.map(task => {
      const processedTask = { ...task };
      if (task.subtasks && task.subtasks.length > 0) {
        processedTask.subtasks = processTasks(task.subtasks);
      }
      return processedTask;
    });
  };
  
  return processTasks(taskTree);
}

// Format due date in human-readable format
export function formatDueDate(dueDateString, isLater = false) {
  if (!dueDateString) return null;
  
  const dateStr = dueDateString.split('T')[0];
  const [year, month, day] = dateStr.split('-');
  const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dueDateNormalized = new Date(dueDate);
  dueDateNormalized.setHours(0, 0, 0, 0);
  
  // If marked as "Later", return "Later" as text but keep date info for tooltip
  if (isLater) {
    const isPast = dueDateNormalized.getTime() < today.getTime();
    return { text: 'Later', isToday: false, isPast: isPast, isTomorrow: false, isLater: true };
  }
  
  if (dueDateNormalized.getTime() === today.getTime()) {
    return { text: 'Today', isToday: true, isPast: false, isTomorrow: false };
  }
  
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  
  if (dueDateNormalized.getTime() === tomorrow.getTime()) {
    return { text: 'Tomorrow', isToday: false, isPast: false, isTomorrow: true };
  }
  
  const isPast = dueDateNormalized.getTime() < today.getTime();
  
  // Check if date is within the next 7 days (excluding today and tomorrow)
  const daysDiff = Math.floor((dueDateNormalized.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  if (!isPast && daysDiff > 0 && daysDiff <= 7) {
    // Return day name for upcoming dates within next 7 days
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[dueDate.getDay()];
    return { text: dayName, isToday: false, isPast: false, isTomorrow: false };
  }
  
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formattedDate = `${monthNames[dueDate.getMonth()]} ${dueDate.getDate()}`;
  
  return { text: formattedDate, isToday: false, isPast: isPast, isTomorrow: false };
}

// Find the previous sibling at the same level
export function findPreviousSibling(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return null;
  
  const siblings = tasks.filter(t => {
    if (currentTask.parent_task_id === null) {
      return t.parent_task_id === null;
    }
    return t.parent_task_id === currentTask.parent_task_id;
  });
  
  siblings.sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  const currentIndex = siblings.findIndex(t => t.id === taskId);
  
  if (currentIndex > 0) {
    return siblings[currentIndex - 1];
  }
  
  return null;
}

// Find the next sibling at the same level
export function findNextSibling(taskId, tasks) {
  const currentTask = tasks.find(t => t.id === taskId);
  if (!currentTask) return null;

  const siblings = tasks.filter(t => {
    if (currentTask.parent_task_id === null) {
      return t.parent_task_id === null;
    }
    return t.parent_task_id === currentTask.parent_task_id;
  });

  siblings.sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });

  const currentIndex = siblings.findIndex(t => t.id === taskId);
  if (currentIndex >= 0 && currentIndex < siblings.length - 1) {
    return siblings[currentIndex + 1];
  }
  return null;
}

// Calculate order for a new task at a given parent level
export function calculateOrderForParent(parentTaskId, tasks, insertAfterTaskId = null) {
  const siblings = tasks.filter(t => {
    if (parentTaskId === null) {
      return t.parent_task_id === null;
    }
    return t.parent_task_id === parentTaskId;
  });
  
  siblings.sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  if (insertAfterTaskId) {
    const insertAfterIndex = siblings.findIndex(t => t.id === insertAfterTaskId);
    if (insertAfterIndex >= 0 && insertAfterIndex < siblings.length - 1) {
      const currentOrder = siblings[insertAfterIndex].order !== null && siblings[insertAfterIndex].order !== undefined 
        ? siblings[insertAfterIndex].order 
        : Number.MAX_SAFE_INTEGER;
      const nextOrder = siblings[insertAfterIndex + 1].order !== null && siblings[insertAfterIndex + 1].order !== undefined
        ? siblings[insertAfterIndex + 1].order
        : Number.MAX_SAFE_INTEGER;
      
      if (nextOrder - currentOrder <= 1) {
        return currentOrder + 1;
      } else {
        return currentOrder + 1;
      }
    }
  }
  
  if (siblings.length > 0) {
    const maxOrder = Math.max(...siblings.map(t => t.order !== null && t.order !== undefined ? t.order : 0));
    return maxOrder + 1;
  }
  return 1;
}

// Calculate order when inserting a task at a specific position (before or after another task)
// Returns the new order value for the task being moved
export function calculateOrderForPosition(parentTaskId, tasks, insertBeforeTaskId = null, insertAfterTaskId = null, excludeTaskId = null) {
  // Get all siblings (excluding the task being moved if specified)
  const siblings = tasks.filter(t => {
    if (excludeTaskId && t.id === excludeTaskId) {
      return false;
    }
    if (parentTaskId === null) {
      return t.parent_task_id === null;
    }
    return t.parent_task_id === parentTaskId;
  });
  
  // Sort siblings by order
  siblings.sort((a, b) => {
    const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
    const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
    if (orderA !== orderB) {
      return orderA - orderB;
    }
    return (a.created_at || '').localeCompare(b.created_at || '');
  });
  
  // If inserting before a specific task
  if (insertBeforeTaskId) {
    const insertBeforeIndex = siblings.findIndex(t => t.id === insertBeforeTaskId);
    if (insertBeforeIndex >= 0) {
      if (insertBeforeIndex === 0) {
        // Inserting at the beginning
        const firstOrder = siblings[0].order !== null && siblings[0].order !== undefined 
          ? siblings[0].order 
          : Number.MAX_SAFE_INTEGER;
        return firstOrder > 1 ? firstOrder - 1 : 0;
      } else {
        // Inserting between two tasks
        const prevOrder = siblings[insertBeforeIndex - 1].order !== null && siblings[insertBeforeIndex - 1].order !== undefined
          ? siblings[insertBeforeIndex - 1].order
          : Number.MAX_SAFE_INTEGER;
        const nextOrder = siblings[insertBeforeIndex].order !== null && siblings[insertBeforeIndex].order !== undefined
          ? siblings[insertBeforeIndex].order
          : Number.MAX_SAFE_INTEGER;
        
        if (nextOrder - prevOrder > 1) {
          // There's space between them
          return prevOrder + 1;
        } else {
          // Need to reorder - return a value that will need recalculation
          return prevOrder + 1;
        }
      }
    }
  }
  
  // If inserting after a specific task
  if (insertAfterTaskId) {
    const insertAfterIndex = siblings.findIndex(t => t.id === insertAfterTaskId);
    if (insertAfterIndex >= 0) {
      if (insertAfterIndex === siblings.length - 1) {
        // Inserting at the end
        const lastOrder = siblings[insertAfterIndex].order !== null && siblings[insertAfterIndex].order !== undefined
          ? siblings[insertAfterIndex].order
          : 0;
        return lastOrder + 1;
      } else {
        // Inserting between two tasks
        const currentOrder = siblings[insertAfterIndex].order !== null && siblings[insertAfterIndex].order !== undefined
          ? siblings[insertAfterIndex].order
          : Number.MAX_SAFE_INTEGER;
        const nextOrder = siblings[insertAfterIndex + 1].order !== null && siblings[insertAfterIndex + 1].order !== undefined
          ? siblings[insertAfterIndex + 1].order
          : Number.MAX_SAFE_INTEGER;
        
        if (nextOrder - currentOrder > 1) {
          // There's space between them
          return currentOrder + 1;
        } else {
          // Need to reorder
          return currentOrder + 1;
        }
      }
    }
  }
  
  // Default: add to end
  if (siblings.length > 0) {
    const maxOrder = Math.max(...siblings.map(t => t.order !== null && t.order !== undefined ? t.order : 0));
    return maxOrder + 1;
  }
  return 1;
}

// Check if moving a task to a new parent would create a circular reference
// (e.g., moving a parent into its own child)
export function isValidTaskMove(taskId, newParentTaskId, tasks) {
  // Can't move a task to be its own parent
  if (taskId === newParentTaskId) {
    return false;
  }
  
  // If new parent is null, it's always valid (moving to root)
  if (newParentTaskId === null) {
    return true;
  }
  
  // Check if newParentTaskId is a descendant of taskId
  // If so, moving would create a circular reference
  const taskTree = buildTaskTree(tasks);
  const findTaskInTree = (taskList, targetId) => {
    for (const task of taskList) {
      if (task.id === targetId) {
        return task;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        const found = findTaskInTree(task.subtasks, targetId);
        if (found) return found;
      }
    }
    return null;
  };
  
  const taskToMove = findTaskInTree(taskTree, taskId);
  if (!taskToMove) {
    return true; // Task not found, allow move
  }
  
  // Check if newParentTaskId is in the subtree of taskToMove
  const checkIfDescendant = (task, targetId) => {
    if (task.id === targetId) {
      return true;
    }
    if (task.subtasks && task.subtasks.length > 0) {
      return task.subtasks.some(subtask => checkIfDescendant(subtask, targetId));
    }
    return false;
  };
  
  return !checkIfDescendant(taskToMove, newParentTaskId);
}

// Get all task IDs that should move with a task (including all subtasks recursively)
export function getTaskAndSubtaskIds(taskId, tasks) {
  const taskTree = buildTaskTree(tasks);
  const allIds = [taskId];
  
  const findTaskAndCollectSubtasks = (taskList) => {
    for (const task of taskList) {
      if (task.id === taskId) {
        const collectSubtasks = (t) => {
          if (t.subtasks && t.subtasks.length > 0) {
            t.subtasks.forEach(subtask => {
              allIds.push(subtask.id);
              collectSubtasks(subtask);
            });
          }
        };
        collectSubtasks(task);
        return true;
      }
      if (task.subtasks && task.subtasks.length > 0) {
        if (findTaskAndCollectSubtasks(task.subtasks)) {
          return true;
        }
      }
    }
    return false;
  };
  
  findTaskAndCollectSubtasks(taskTree);
  return allIds;
}

// Parse human-readable date string (e.g., "Jan 6", "Feb 10", "March 15") to ISO date string
export function parseHumanReadableDate(dateStr) {
  if (!dateStr || typeof dateStr !== 'string') return null;
  
  const trimmed = dateStr.trim();
  if (!trimmed) return null;
  
  const monthNames = [
    'january', 'february', 'march', 'april', 'may', 'june',
    'july', 'august', 'september', 'october', 'november', 'december'
  ];
  
  const monthAbbrevs = [
    'jan', 'feb', 'mar', 'apr', 'may', 'jun',
    'jul', 'aug', 'sep', 'oct', 'nov', 'dec'
  ];
  
  // Try to match patterns like "Jan 6", "January 6", "Jan 06", etc.
  const patterns = [
    /^([a-z]+)\s+(\d+)$/i,  // "Jan 6" or "January 6"
    /^(\d+)\s+([a-z]+)$/i,  // "6 Jan" or "6 January"
  ];
  
  for (const pattern of patterns) {
    const match = trimmed.match(pattern);
    if (match) {
      let monthStr, dayStr;
      
      if (pattern === patterns[0]) {
        monthStr = match[1].toLowerCase();
        dayStr = match[2];
      } else {
        dayStr = match[1];
        monthStr = match[2].toLowerCase();
      }
      
      // Find month index
      let monthIndex = monthAbbrevs.findIndex(m => m === monthStr);
      if (monthIndex === -1) {
        monthIndex = monthNames.findIndex(m => m === monthStr);
      }
      
      if (monthIndex === -1) {
        // Try matching first 3 characters
        const firstThree = monthStr.substring(0, 3);
        monthIndex = monthAbbrevs.findIndex(m => m === firstThree);
        if (monthIndex === -1) {
          monthIndex = monthNames.findIndex(m => m.startsWith(firstThree));
        }
      }
      
      if (monthIndex === -1) return null;
      
      const day = parseInt(dayStr, 10);
      if (isNaN(day) || day < 1 || day > 31) return null;
      
      // Get current year, or next year if the date has already passed this year
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentYear = today.getFullYear();
      const date = new Date(currentYear, monthIndex, day);
      date.setHours(0, 0, 0, 0);
      
      // If the date has already passed this year, use next year
      if (date < today) {
        date.setFullYear(currentYear + 1);
      }
      
      // Format as ISO date string (YYYY-MM-DD)
      const year = date.getFullYear();
      const month = String(monthIndex + 1).padStart(2, '0');
      const dayPadded = String(day).padStart(2, '0');
      
      return `${year}-${month}-${dayPadded}`;
    }
  }
  
  return null;
}

// Parse pasted content to extract tasks with hierarchy
export function parsePastedContent(text) {
  const lines = text.split(/\r?\n/);
  const parsedTasks = [];
  
  for (const line of lines) {
    if (!line.trim()) continue;
    
    let indentLevel = 0;
    let remainingLine = line;
    
    while (remainingLine.length > 0) {
      if (remainingLine[0] === '\t') {
        indentLevel++;
        remainingLine = remainingLine.substring(1);
      } else if (remainingLine.startsWith('    ')) {
        indentLevel++;
        remainingLine = remainingLine.substring(4);
      } else if (remainingLine.startsWith('  ')) {
        indentLevel++;
        remainingLine = remainingLine.substring(2);
      } else {
        break;
      }
    }
    
    let taskText = remainingLine.trim();
    let status = 'incomplete';
    let dueDate = null;
    
    const checkboxMatch = taskText.match(/^-\s*\[([xX\s])\]\s*(.+)$/);
    if (checkboxMatch) {
      const checkboxState = checkboxMatch[1].toLowerCase();
      status = checkboxState === 'x' ? 'complete' : 'incomplete';
      taskText = checkboxMatch[2].trim();
    } else {
      const bulletMatch = taskText.match(/^-\s+(.+)$/);
      if (bulletMatch) {
        taskText = bulletMatch[1].trim();
      }
    }
    
    // Check for date in parentheses at the end of the line
    const dateMatch = taskText.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (dateMatch) {
      const dateStr = dateMatch[2].trim();
      const parsedDate = parseHumanReadableDate(dateStr);
      if (parsedDate) {
        taskText = dateMatch[1].trim();
        dueDate = parsedDate;
      }
    }
    
    if (!taskText) continue;
    
    parsedTasks.push({
      text: taskText,
      indentLevel: indentLevel,
      status: status,
      dueDate: dueDate
    });
  }
  
  return parsedTasks;
}

// Parse text and convert URLs to React elements
export function parseTextWithUrls(text) {
  if (!text) return [];
  
  // URL regex pattern - matches http://, https://, and www. URLs
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const parts = [];
  let lastIndex = 0;
  let match;
  let key = 0;
  
  while ((match = urlRegex.exec(text)) !== null) {
    // Add text before the URL
    if (match.index > lastIndex) {
      const textBefore = text.substring(lastIndex, match.index);
      if (textBefore) {
        parts.push({ type: 'text', content: textBefore, key: key++ });
      }
    }
    
    // Add the URL as a link
    let url = match[0];
    let href = url;
    
    // Add protocol if missing (for www. URLs)
    if (!url.match(/^https?:\/\//i)) {
      href = 'https://' + url;
    }
    
    parts.push({ type: 'link', content: url, href: href, key: key++ });
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    const textAfter = text.substring(lastIndex);
    if (textAfter) {
      parts.push({ type: 'text', content: textAfter, key: key++ });
    }
  }
  
  // If no URLs were found, return the whole text as a single text part
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text, key: key++ });
  }
  
  return parts;
}

// Max length for link display text when not editing (href stays full). Exported for use in TaskText.
export const MAX_LINK_DISPLAY_LENGTH = 120;

// Convert text with URLs to HTML string
// options.maxLinkDisplayLength - when set, truncate visible link text to this length (for display mode)
export function textWithUrlsToHtml(text, options = {}) {
  if (!text) return '';
  
  const { maxLinkDisplayLength } = options;
  const parts = parseTextWithUrls(text);
  let html = '';
  
  for (const part of parts) {
    if (part.type === 'link') {
      let displayContent = part.content;
      if (typeof maxLinkDisplayLength === 'number' && part.content.length > maxLinkDisplayLength) {
        displayContent = part.content.substring(0, maxLinkDisplayLength) + '…';
      }
      const escapedUrl = displayContent.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      const escapedHref = part.href.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
      html += `<a href="${escapedHref}" target="_blank" rel="noopener noreferrer" class="task-text-link" onclick="event.stopPropagation(); return true;">${escapedUrl}</a>`;
    } else {
      const escaped = part.content.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      html += escaped;
    }
  }
  
  return html;
}

// Sequential task functions have been moved to ./sequentialTasks.js
// Import them from there: isSequentialTask, getSequenceNumber, getSequentialTaskGroup,
// canCompleteSequentialTask, isBlockedByParentSequentialTask,
// canCompleteTask, getBlockingTasks

// Get all due dates from subtasks recursively (only from incomplete tasks)
export function getAllSubtaskDueDates(task) {
  const dueDates = [];
  
  if (!task.subtasks || task.subtasks.length === 0) {
    return dueDates;
  }
  
  // Helper to check if a subtask is complete
  const isSubtaskComplete = (subtask) => {
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      const stats = getSubtaskStats(subtask);
      return stats.total > 0 && stats.completed === stats.total;
    } else {
      return subtask.status === 'complete' || subtask.status === 'wont_do';
    }
  };
  
  const collectDueDates = (subtask) => {
    // Only collect due dates from incomplete tasks
    if (!isSubtaskComplete(subtask)) {
      if (subtask.due_date) {
        dueDates.push(subtask.due_date);
      }
    }
    // Continue recursively through subtasks
    if (subtask.subtasks && subtask.subtasks.length > 0) {
      subtask.subtasks.forEach(collectDueDates);
    }
  };
  
  task.subtasks.forEach(collectDueDates);
  
  // Remove duplicates and sort
  const uniqueDates = [...new Set(dueDates)].sort();
  return uniqueDates;
}

// Format multiple due dates for display
export function formatMultipleDueDates(dueDates) {
  if (!dueDates || dueDates.length === 0) return null;
  
  const formattedDates = dueDates.map(date => {
    const formatted = formatDueDate(date);
    return formatted ? formatted.text : null;
  }).filter(date => date !== null);
  
  if (formattedDates.length === 0) return null;
  
  // Remove duplicates
  const uniqueFormatted = [...new Set(formattedDates)];
  
  if (uniqueFormatted.length === 1) {
    return uniqueFormatted[0];
  }
  
  return uniqueFormatted.join(', ');
}

// Check if a task should be shown in prioritize view
// Returns true if task should be shown (not completed, not wont_do, not blocked sequential)
export function shouldShowInPrioritize(task, tasks) {
  // Check if task is blocked by parent sequential task
  if (isBlockedByParentSequentialTask(task.id, tasks)) {
    return false;
  }
  
  // Check if task is completed or wont_do
  if (task.subtasks && task.subtasks.length > 0) {
    const stats = getSubtaskStats(task);
    // If all subtasks are completed, hide the task
    if (stats.total > 0 && stats.completed === stats.total) {
      return false;
    }
    
    // Check if this parent task itself is a blocked sequential task
    if (isSequentialTask(task)) {
      const canComplete = canCompleteTask(task.id, tasks);
      if (!canComplete) {
        return false;
      }
    }
    
    // Check if any subtask should be shown
    return task.subtasks.some(subtask => shouldShowInPrioritize(subtask, tasks));
  } else {
    // Leaf task - check status
    if (task.status === 'complete' || task.status === 'wont_do') {
      return false;
    }
    // Check if blocked sequential task
    if (isSequentialTask(task)) {
      const canComplete = canCompleteTask(task.id, tasks);
      if (!canComplete) {
        return false;
      }
    }
    return true;
  }
}

// Get prioritize section for a task based on its due date
// Returns: 'no-due-date', 'today', 'later'
// Overdue tasks are included in 'no-due-date' section
export function getPrioritizeSection(task) {
  if (!task.due_date) {
    return 'no-due-date';
  }
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dateStr = task.due_date.split('T')[0];
  const [year, month, day] = dateStr.split('-');
  const dueDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  dueDate.setHours(0, 0, 0, 0);
  
  const daysDiff = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // Overdue tasks go into 'no-due-date' section
  if (daysDiff < 0) {
    return 'no-due-date';
  } else if (daysDiff === 0) {
    return 'today';
  } else {
    // All tasks due after today go into 'later'
    return 'later';
  }
}

// Effective section when "Work in progress" is visible: wip tasks belong to 'work-in-progress', not their due-date section.
// When "Done" is visible: completed leaf tasks belong to 'done'.
// visiblePrioritizeSections: Set of section keys that are visible (e.g. from usePrioritizeSections).
export function getEffectivePrioritizeSection(task, visiblePrioritizeSections = null) {
  if (visiblePrioritizeSections && visiblePrioritizeSections.has('done') && task.status === 'complete') {
    return 'done';
  }
  if (visiblePrioritizeSections && visiblePrioritizeSections.has('work-in-progress') && task.status === 'wip') {
    return 'work-in-progress';
  }
  return getPrioritizeSection(task);
}

// Get the earliest due date from a task or its subtasks
// Only considers subtasks that should be shown in prioritize (for prioritize grouping)
export function getEarliestDueDate(task, tasks = null, onlyVisibleSubtasks = false) {
  if (task.due_date) {
    return task.due_date;
  }
  
  if (task.subtasks && task.subtasks.length > 0) {
    const subtaskDueDates = [];
    const collectDueDates = (subtask) => {
      // If onlyVisibleSubtasks is true, only collect from subtasks that should be shown
      if (onlyVisibleSubtasks && tasks) {
        if (!shouldShowInPrioritize(subtask, tasks)) {
          // Skip this subtask, but still check its subtasks
          if (subtask.subtasks && subtask.subtasks.length > 0) {
            subtask.subtasks.forEach(collectDueDates);
          }
          return;
        }
      }
      
      if (subtask.due_date) {
        subtaskDueDates.push(subtask.due_date);
      }
      if (subtask.subtasks && subtask.subtasks.length > 0) {
        subtask.subtasks.forEach(collectDueDates);
      }
    };
    task.subtasks.forEach(collectDueDates);
    
    if (subtaskDueDates.length > 0) {
      return subtaskDueDates.sort()[0]; // Return earliest date
    }
  }
  
  return null;
}

// Group tasks by prioritize sections
// Returns an object with section keys and arrays of tasks
// Only includes leaf tasks (tasks without subtasks), not parent tasks
// Note: Overdue tasks are merged into 'today' section
// forceIncludeTaskIds: optional Set of task ids to include even when completed (e.g. for completion animation delay)
// visiblePrioritizeSections: optional Set; when it contains 'work-in-progress', wip tasks go in that section and are excluded from others
export function groupTasksByPrioritize(taskTree, tasks, forceIncludeTaskIds = null, visiblePrioritizeSections = null) {
  const showWorkInProgress = visiblePrioritizeSections && visiblePrioritizeSections.has('work-in-progress');
  const showDone = visiblePrioritizeSections && visiblePrioritizeSections.has('done');
  const sections = {
    'no-due-date': [],
    'today': [],
    'later': []
  };
  if (showWorkInProgress) {
    sections['work-in-progress'] = [];
  }
  if (showDone) {
    sections['done'] = [];
  }

  // Recursively collect all leaf tasks (tasks without subtasks)
  const collectLeafTasks = (taskList) => {
    const leafTasks = [];
    
    const traverse = (task) => {
      // Check if task has subtasks
      const hasSubtasks = task.subtasks && task.subtasks.length > 0;
      
      if (hasSubtasks) {
        // Don't include this task, but traverse its subtasks
        task.subtasks.forEach(traverse);
      } else {
        // This is a leaf task, include it if it should be shown, force-included (e.g. recently completed), or completed (for Done section)
        const forceInclude = forceIncludeTaskIds && forceIncludeTaskIds.has(task.id);
        if (shouldShowInPrioritize(task, tasks) || forceInclude || (showDone && task.status === 'complete')) {
          leafTasks.push(task);
        }
      }
    };
    
    taskList.forEach(traverse);
    return leafTasks;
  };
  
  // Collect all leaf tasks from the tree
  const leafTasks = collectLeafTasks(taskTree);
  
  // Group leaf tasks by prioritize section
  // When a task was just completed, keep it in its previous section until the completion animation + delay (forceIncludeTaskIds)
  leafTasks.forEach(task => {
    const recentlyCompleted = forceIncludeTaskIds && forceIncludeTaskIds.has(task.id);
    if (showDone && task.status === 'complete' && !recentlyCompleted) {
      sections['done'].push(task);
      return;
    }
    if (showWorkInProgress && task.status === 'wip') {
      sections['work-in-progress'].push(task);
      return;
    }
    // Only use the task's own due date
    // If task doesn't have its own due date, it goes to "No due date" section
    const section = getPrioritizeSection(task);
    sections[section].push(task);
  });
  
  // Sort tasks within each section by prioritize_order (if set), then by order, then by due date
  Object.keys(sections).forEach(section => {
    const sectionTasks = sections[section];
    sectionTasks.sort((a, b) => {
      // First, sort by prioritize_order (if set)
      const prioritizeOrderA = a.prioritize_order !== null && a.prioritize_order !== undefined 
        ? a.prioritize_order 
        : null;
      const prioritizeOrderB = b.prioritize_order !== null && b.prioritize_order !== undefined 
        ? b.prioritize_order 
        : null;
      
      if (prioritizeOrderA !== null && prioritizeOrderB !== null) {
        return prioritizeOrderA - prioritizeOrderB;
      }
      if (prioritizeOrderA !== null) return -1; // Tasks with prioritize_order come first
      if (prioritizeOrderB !== null) return 1;
      
      // If no prioritize_order, fall back to order
      const orderA = a.order !== null && a.order !== undefined ? a.order : Number.MAX_SAFE_INTEGER;
      const orderB = b.order !== null && b.order !== undefined ? b.order : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) {
        return orderA - orderB;
      }
      
      // If same order, sort by due date (for today and later sections)
      if (section !== 'no-due-date') {
        const dateA = a.due_date;
        const dateB = b.due_date;
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.localeCompare(dateB);
      }
      
      // For no-due-date, use created_at as final tiebreaker
      return (a.created_at || '').localeCompare(b.created_at || '');
    });
  });
  
  return sections;
}

// Get section display name
export function getPrioritizeSectionName(section) {
  const names = {
    'work-in-progress': 'Work in progress',
    'no-due-date': 'Unprioritized',
    'overdue': 'Overdue',
    'today': 'Today',
    'later': 'Later',
    'done': 'Done'
  };
  return names[section] || section;
}

// Get section display order
export function getPrioritizeSectionOrder(section) {
  const order = {
    'no-due-date': 0,
    'today': 1,
    'work-in-progress': 2,
    'later': 3,
    'done': 4
  };
  return order[section] !== undefined ? order[section] : 999;
}

// Get parent task chain for a task (all ancestors up to root)
// Returns array of parent tasks from root to immediate parent
export function getParentTaskChain(taskId, tasks) {
  const chain = [];
  const task = tasks.find(t => t.id === taskId);
  if (!task || !task.parent_task_id) {
    return chain;
  }
  
  let parentId = task.parent_task_id;
  while (parentId !== null && parentId !== undefined) {
    const parentTask = tasks.find(t => t.id === parentId);
    if (!parentTask) break;
    
    chain.unshift(parentTask); // Add to beginning to maintain root-to-parent order
    parentId = parentTask.parent_task_id;
  }
  
  return chain;
}

// Detect and parse human-readable dates from task text
// Returns a date string in YYYY-MM-DD format, or null if no date is detected
export function detectDateInText(text) {
  if (!text || typeof text !== 'string') return null;
  
  const trimmedText = text.trim();
  if (!trimmedText) return null;
  
  // Day names (case-insensitive)
  const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const dayNamesShort = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  
  // Month names (case-insensitive)
  const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 
                      'july', 'august', 'september', 'october', 'november', 'december'];
  const monthNamesShort = ['jan', 'feb', 'mar', 'apr', 'may', 'jun',
                           'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
  
  // Helper to format date as YYYY-MM-DD
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const textLower = trimmedText.toLowerCase();
  
  // Check for month + day patterns first (more specific than day names)
  // Pattern: Month (name or abbreviation) + day number (optional year)
  // Examples: "Jan 19", "January 19", "Jan 19th", "Jan 19, 2024"
  const monthDayPattern = new RegExp(
    `\\b(${monthNamesShort.join('|')}|${monthNames.join('|')})\\s+(\\d{1,2})(?:st|nd|rd|th)?(?:,\\s*(\\d{4}))?\\b`,
    'i'
  );
  
  const monthDayMatch = trimmedText.match(monthDayPattern);
  if (monthDayMatch) {
    const monthStr = monthDayMatch[1].toLowerCase();
    const day = parseInt(monthDayMatch[2], 10);
    const year = monthDayMatch[3] ? parseInt(monthDayMatch[3], 10) : null;
    
    // Find month index
    let monthIndex = monthNamesShort.findIndex(m => m === monthStr);
    if (monthIndex === -1) {
      monthIndex = monthNames.findIndex(m => m === monthStr);
    }
    
    if (monthIndex !== -1 && day >= 1 && day <= 31) {
      const today = new Date();
      const currentYear = today.getFullYear();
      const targetYear = year || currentYear;
      
      // If no year specified and the date has already passed this year, use next year
      if (!year) {
        const testDate = new Date(targetYear, monthIndex, day);
        if (testDate < today) {
          const nextYearDate = new Date(targetYear + 1, monthIndex, day);
          return formatDate(nextYearDate);
        }
      }
      
      const targetDate = new Date(targetYear, monthIndex, day);
      // Validate the date (handles invalid dates like Feb 30)
      if (targetDate.getMonth() === monthIndex && targetDate.getDate() === day) {
        return formatDate(targetDate);
      }
    }
  }
  
  // Check for day names (e.g., "Monday", "monday", "Mon", "mon")
  // Only match if the day name is at the end of the text or is a standalone word
  // Check for full day names - match if text ends with the day name or contains it as a standalone word
  for (let i = 0; i < dayNames.length; i++) {
    const dayName = dayNames[i];
    // Match if: text is exactly the day name, or text ends with the day name, or day name appears as a word boundary
    const dayPattern = new RegExp(`(^|\\s)${dayName}(\\s|$|[.,!?;:])`, 'i');
    if (textLower === dayName || textLower.endsWith(dayName) || dayPattern.test(trimmedText)) {
      // Find next occurrence of this day
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentDay = today.getDay();
      let daysUntil = i - currentDay;
      if (daysUntil <= 0) {
        daysUntil += 7; // Next week
      }
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntil);
      return formatDate(targetDate);
    }
  }
  
  // Check for short day names - match if text ends with the day name or contains it as a standalone word
  for (let i = 0; i < dayNamesShort.length; i++) {
    const dayName = dayNamesShort[i];
    // Match if: text is exactly the day name, or text ends with the day name, or day name appears as a word boundary
    const dayPattern = new RegExp(`(^|\\s)${dayName}(\\s|$|[.,!?;:])`, 'i');
    if (textLower === dayName || textLower.endsWith(dayName) || dayPattern.test(trimmedText)) {
      // Find next occurrence of this day
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const currentDay = today.getDay();
      let daysUntil = i - currentDay;
      if (daysUntil <= 0) {
        daysUntil += 7; // Next week
      }
      const targetDate = new Date(today);
      targetDate.setDate(today.getDate() + daysUntil);
      return formatDate(targetDate);
    }
  }
  
  return null;
}

